import { useEffect, useRef, useState } from 'react'
import type { CotizacionDTO, EmpresaDTO, SendCotizacionRequest, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import {
    dueView,
    errorMessage,
    personName,
    quoteInsured,
    quoteStatusView,
    QUOTE_VIEWER_PATH,
    ramoFields,
    toProposal,
    trackingView,
} from '../common/quote-common'

export type { QuoteDetailValue, QuotePaymentPlan, QuoteProposal } from '../common/quote-common'

export type QuoteDrawerTab = 'general' | 'propuestas'

export interface QuoteDrawerProps {
    cotizacionId: number | null
    onClose: () => void
    onEdit?: (cotizacionId: number) => void
    onDeleted?: (cotizacion?: CotizacionDTO | null) => void
    /** Tras enviar la cotización al cliente (cambia el seguimiento en el listado). */
    onChanged?: () => void
}

const DETAIL_ERROR = 'No se pudo cargar el detalle de la cotización.'
const DELETE_ERROR = 'No se pudo eliminar la cotización.'
const SEND_ERROR = 'No se pudo enviar la cotización.'

export const useQuoteDrawerModel = ({ cotizacionId, onClose, onDeleted, onChanged }: QuoteDrawerProps) => {
    const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [cotizacion, setCotizacion] = useState<CotizacionDTO | null>(null)
    const [empresa, setEmpresa] = useState<EmpresaDTO | null | undefined>(undefined)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [tab, setTab] = useState<QuoteDrawerTab>('general')
    const [toast, setToast] = useState<string | null>(null)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [confirmSend, setConfirmSend] = useState(false)
    const [sending, setSending] = useState(false)

    const showToast = (message: string) => {
        clearTimeout(toastTimer.current)
        setToast(message)
        toastTimer.current = setTimeout(() => setToast(null), 3000)
    }

    useEffect(() => () => clearTimeout(toastTimer.current), [])

    useEffect(() => {
        if (!cotizacionId) return undefined
        setTab('general')
        setCotizacion(null)
        setError(null)
        setConfirmDelete(false)
        setConfirmSend(false)
        return undefined
    }, [cotizacionId])

    useEffect(() => {
        if (!cotizacionId) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/cotizaciones/${cotizacionId}`)
                const payload: WrapperResponse<CotizacionDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || DETAIL_ERROR)
                setCotizacion(payload.body)
            } catch (loadError) {
                if (active) {
                    setCotizacion(null)
                    setError(errorMessage(loadError, DETAIL_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [cotizacionId, reloadToken])

    useEffect(() => {
        if (!cotizacionId || empresa !== undefined) return undefined
        let active = true
        const load = async () => {
            try {
                const response = await APIInvoke.invokeGET('/empresas/default')
                const payload: WrapperResponse<EmpresaDTO | null> | null = await response.json()
                if (active && payload?.ok) setEmpresa(payload.body)
            } catch {
                if (active) setEmpresa(undefined)
            }
        }
        load()
        return () => { active = false }
    }, [cotizacionId, empresa])

    const deleteCotizacion = async () => {
        if (!cotizacion?.id || deleting) return
        try {
            setConfirmDelete(false)
            setDeleting(true)
            const payload: WrapperResponse<CotizacionDTO> | null = await (await APIInvoke.invokeDELETE(`/cotizaciones/${cotizacion.id}`)).json()
            if (!payload?.ok) throw new Error(payload?.message || DELETE_ERROR)
            onDeleted?.(cotizacion)
            onClose()
        } catch (deleteError) {
            showToast(errorMessage(deleteError, DELETE_ERROR))
        } finally {
            setDeleting(false)
        }
    }

    const sendCotizacion = async () => {
        if (!cotizacion?.id || !cotizacion.correo || sending) return
        try {
            setConfirmSend(false)
            setSending(true)
            const request: SendCotizacionRequest = { correo: cotizacion.correo }
            const payload: WrapperResponse<unknown> | null = await (await APIInvoke.invokePOST(`/cotizaciones/${cotizacion.id}/send`, request)).json()
            if (!payload?.ok) throw new Error(payload?.message || SEND_ERROR)
            showToast(`Cotización enviada a ${cotizacion.correo}`)
            setReloadToken((token) => token + 1)
            onChanged?.()
        } catch (sendError) {
            showToast(errorMessage(sendError, SEND_ERROR))
        } finally {
            setSending(false)
        }
    }

    const fields = ramoFields(cotizacion?.ramo)
    const contratante = cotizacion?.cliente || cotizacion?.prospecto || null
    const proposals = (cotizacion?.items || []).map((item, index) => toProposal(item, index, cotizacion?.ramo))
    const tracking = trackingView(cotizacion?.fechaVista, cotizacion?.fechaEnvio)

    return {
        cotizacion,
        loading,
        error,
        tab,
        tabs: [
            { id: 'general' as QuoteDrawerTab, label: 'Datos generales', count: null },
            { id: 'propuestas' as QuoteDrawerTab, label: 'Propuestas', count: cotizacion ? proposals.length : null },
        ],
        toast,
        name: contratante ? personName(contratante) : '',
        clienteId: cotizacion?.cliente?.id ?? null,
        tipo: cotizacion?.cliente ? 'Cliente' : 'Prospecto',
        status: cotizacion ? quoteStatusView(cotizacion.status) : null,
        vencimiento: dueView(cotizacion?.fecVencimiento, cotizacion?.status),
        tracking,
        trackingText: cotizacion ? [tracking.label, tracking.detail].filter(Boolean).join(' · ') : '',
        empresaMissing: empresa === null,
        fields,
        asegurados: (cotizacion?.asegurados || []).map((item, index) => {
            const row = quoteInsured(item, index)
            return {
                ...row,
                detail: [row.genero, row.nacimiento ? `Nacimiento ${row.nacimiento}` : null, row.edad != null ? `${row.edad} años` : null]
                    .filter(Boolean)
                    .join(' · ') || 'Sin datos adicionales',
            }
        }),
        vehiculos: (cotizacion?.vehiculos || []).map((item, index) => ({
            key: String(item.id ?? index),
            descripcion: item.descripcion || '—',
            modelo: item.modelo,
        })),
        proposals,
        viewerUrl: cotizacion?.uuid ? `${QUOTE_VIEWER_PATH}/${cotizacion.uuid}` : null,
        confirmDelete,
        deleting,
        confirmSend,
        sending,
        setTab,
        reload: () => setReloadToken((token) => token + 1),
        askDelete: () => setConfirmDelete(true),
        cancelDelete: () => setConfirmDelete(false),
        askSend: () => setConfirmSend(true),
        cancelSend: () => setConfirmSend(false),
        deleteCotizacion,
        sendCotizacion,
    }
}
